/**
 * HarpEngine.js
 * Generates idiomatic Harp glissandi and textures.
 */

const HARP_CONFIG = {
    glissandoMin: 36, // C2
    glissandoMax: 96, // C7
    noteDuration: 0.03125 // 1/32 note
};

const BISBIGLIANDO_MIN_PITCH = 72; // C5

// Helper: Get Scale Notes extended across octaves
const getExtendedScale = (root, scaleType, range) => {
    // Basic Major/Minor Logic if SCALES undefined or not matching
    // Assuming simple Diatonic for Gliss
    const scaleIndices = (scaleType === 'minor')
        ? [0, 2, 3, 5, 7, 8, 10]
        : [0, 2, 4, 5, 7, 9, 11]; // Major

    // Normalize Root
    const roots = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    let rootIdx = roots.indexOf(root);
    if (rootIdx === -1) rootIdx = 0; // Default C

    const extended = [];
    for (let oct = 1; oct <= 8; oct++) { // Extended to C8 to catch high notes
        scaleIndices.forEach(interval => {
            const note = (oct * 12) + rootIdx + interval;
            if (note >= range.min && note <= range.max) {
                extended.push(note);
            }
        });
    }
    return extended;
};

// Helper for fuzzing values
const fuzz = (value, variance, random) => {
    if (!random) return value;
    const range = variance * 2;
    return value + ((random.next() * range) - variance);
};

// Safe Modulo for negative numbers
const safeMod = (n, m) => ((n % m) + m) % m;

/**
 * Generates a Harp Pattern (Task 3: Smart Harp).
 * Supports glissando, arpeggio, bisbigliando, and cascade strategies.
 * @param {string} rootKey - e.g., 'C', 'F#'
 * @param {string} scaleType - 'major', 'minor'
 * @param {number} startTime - Absolute start time in bars/beats
 * @param {number} requestedDuration - Duration in bars/beats
 * @param {string} direction - 'up', 'down', 'updown'
 * @param {Object} context - { tension, tempo, random, strategy, section }
 */
export const generateHarpPattern = (rootKey, scaleType, startTime, requestedDuration, direction = 'up', context = {}) => {
    const tension = context.tension || 0.5;
    const tempo = context.tempo || 120;
    let strategy = context.strategy || 'gliss';

    // Inject Randomness
    const rng = context.random ? { next: () => context.random.next() } : { next: Math.random };

    // Smart Strategy Selection based on Section/Context
    if (context.section === 'INTRO' || context.section === 'VERSE') {
        if (rng.next() > 0.5) strategy = 'wide_arpeggio';
        else strategy = 'ethereal_swells';
    } else if (context.section === 'CLIMAX' || context.section === 'DROP') {
        if (rng.next() > 0.6) strategy = 'gliss'; // Standard sweep
        else strategy = 'cascade'; // Driving wave
    } else if (tension < 0.3) {
        strategy = 'bisbigliando'; // Delicate
    }

    let duration = requestedDuration || 4.0;

    // Dynamic calculation for Glissando duration scaling
    if (context.tempo && strategy === 'gliss') {
        const speedFactor = tempo / 120.0;
        const tensionFactor = (tension > 0.6) ? 1.0 : 2.0; // Fast vs Slow
        duration = speedFactor * tensionFactor * 2.0; // Base 2 bars
    }

    const range = { min: HARP_CONFIG.glissandoMin, max: HARP_CONFIG.glissandoMax };
    if (duration <= 0) return [];

    const pool = getExtendedScale(rootKey, scaleType, range);
    if (pool.length < 2) return [];

    const events = [];

    // --- STRATEGY DISPATCHER ---

    if (strategy === 'wide_arpeggio') {
        // Epic Cinematic Intro Style: Low -> High -> Low (2-3 Octaves)
        // Slower, rhythmic (8th or 16th triplets)
        const noteResolution = 0.166; // Triplet 16th approx (1/6)
        const noteCount = Math.floor(duration / noteResolution);

        // Select a wide subset
        const center = Math.floor(pool.length / 2);
        const widePool = pool.slice(Math.max(0, center - 10), Math.min(pool.length, center + 10));

        let idx = 0;
        let dir = 1;

        for (let i = 0; i < noteCount; i++) {
            const time = startTime + (i * noteResolution);

            // Ping Pong
            const note = widePool[idx];

            events.push({
                s: time,
                d: note,
                len: 0.3,
                vel: 0.5 + (Math.sin(i * 0.1) * 0.2) + fuzz(0, 0.05, rng),
                articulation: 'pluck'
            });

            idx += dir;
            if (idx >= widePool.length - 1 || idx <= 0) dir *= -1;
        }

    } else if (strategy === 'ethereal_swells') {
        // Random clusters washing in and out
        const numSwells = Math.floor(duration / 2.0); // One swell every 2 bars?

        for (let s = 0; s < numSwells; s++) {
            const swellStart = startTime + (s * 2.0) + (rng.next() * 0.5);
            // Pick a register
            const regStart = Math.floor(rng.next() * (pool.length - 10));
            const notes = pool.slice(regStart, regStart + 5);

            // Generate 5-7 notes rapidly
            notes.forEach((n, i) => {
                events.push({
                    s: swellStart + (i * 0.05) + fuzz(0, 0.02, rng), // Humanize
                    d: n,
                    len: 1.5, // Ring out
                    vel: 0.3 + (i * 0.05), // Fade in
                    articulation: 'pluck'
                });
            });
        }

    } else if (strategy === 'arpeggio') {
        // Standard Arp (Existing Logic)
        const noteResolution = 0.25;
        const noteCount = Math.floor(duration / noteResolution);
        const arpPool = pool.filter((_, i) => i % 2 === 0);
        if (arpPool.length === 0) return [];

        let currentIdx = (direction === 'down') ? arpPool.length - 1 : 0;
        let currentDirection = direction;
        const startOffset = Math.floor(rng.next() * (arpPool.length / 2));
        currentIdx = (direction === 'down') ? (arpPool.length - 1 - startOffset) : startOffset;

        for (let i = 0; i < noteCount; i++) {
            const time = startTime + (i * noteResolution);
            let note = arpPool[safeMod(currentIdx, arpPool.length)];
            const vel = 0.6 + (Math.sin(i * 0.5) * 0.2) + fuzz(0, 0.05, rng);

            events.push({
                s: time, d: note, len: 0.2, vel: Math.min(1.0, vel), articulation: 'pluck'
            });

            if (currentDirection === 'down') currentIdx--; else currentIdx++;
            if (currentIdx < 0) { currentIdx = 1; currentDirection = 'up'; }
            if (currentIdx >= arpPool.length) { currentIdx = arpPool.length - 2; currentDirection = 'down'; }
        }

    } else if (strategy === 'bisbigliando') {
        // Existing Logic
        const noteResolution = 0.125;
        const noteCount = Math.floor(duration / noteResolution);
        const highPool = pool.filter(n => n > BISBIGLIANDO_MIN_PITCH);
        if (highPool.length < 2) return [];

        const centerIdx = Math.floor(rng.next() * (highPool.length - 1));
        const notes = [highPool[centerIdx], highPool[centerIdx + 1]];

        for (let i = 0; i < noteCount; i++) {
            const time = startTime + (i * noteResolution);
            const note = notes[i % 2];
            const vel = 0.3 + (Math.sin(i * 0.2) * 0.1) + fuzz(0, 0.05, rng);
            events.push({ s: time, d: note, len: 0.1, vel: vel, articulation: 'pluck' });
        }

    } else if (strategy === 'cascade') {
        // Existing Logic
        const res = 0.125;
        const noteCount = Math.floor(duration / res);
        const waveHeight = 12;
        const waveCenter = Math.floor(pool.length / 2);

        for (let i = 0; i < noteCount; i++) {
            const time = startTime + (i * res);
            const wavePos = Math.sin(i * 0.2);
            const offset = Math.floor(wavePos * (waveHeight / 2));
            const noteIdx = Math.max(0, Math.min(pool.length-1, waveCenter + offset));
            const vel = 0.5 + (wavePos * 0.3) + fuzz(0, 0.05, rng);
            events.push({ s: time, d: pool[noteIdx], len: 0.1, vel: Math.min(1.0, vel), articulation: 'pluck' });
        }

    } else {
        // DEFAULT GLISSANDO (Existing Logic)
        const noteResolution = (tension > 0.7) ? 0.03125 : 0.0625;
        const noteCount = Math.floor(duration / noteResolution);
        let startOffset = 0;
        let endOffset = 0;
        if (tension < 0.5) {
            const center = Math.floor(pool.length / 2);
            const variance = Math.floor(pool.length * 0.3);
            const shift = Math.floor((rng.next() - 0.5) * variance);
            startOffset = Math.max(0, Math.min(pool.length - 5, center + shift - 10));
            endOffset = Math.max(0, Math.min(pool.length - 1, center + shift + 10));
        } else {
            startOffset = 0;
            endOffset = pool.length - 1;
        }
        const subsetSize = endOffset - startOffset;
        if (subsetSize < 2) return [];
        const stepSize = subsetSize / noteCount;
        let currentSubsetIndex = (direction === 'down') ? subsetSize : 0;
        let currentDirection = direction;

        for (let i = 0; i < noteCount; i++) {
            const time = startTime + (i * noteResolution);
            let relativeIdx = Math.floor(currentSubsetIndex);
            if (relativeIdx < 0) relativeIdx = 0;
            if (relativeIdx > subsetSize) relativeIdx = subsetSize;
            let poolIdx = startOffset + relativeIdx;
            if (poolIdx >= pool.length) poolIdx = pool.length - 1;
            const baseVel = 0.3 + (tension * 0.4);
            const noise = (rng.next() - 0.5) * 0.2;
            const arc = Math.sin(i / noteCount * Math.PI) * 0.3;
            const velocity = Math.min(1.0, baseVel + arc + noise);
            events.push({ s: time, d: pool[poolIdx], len: 0.1, vel: velocity, articulation: 'pluck' });
            if (currentDirection === 'up') currentSubsetIndex += stepSize;
            else currentSubsetIndex -= stepSize;
        }
    }

    return events;
};
